import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FiUsers, FiImage, FiFolderPlus, FiTag, FiShare2, FiEye, FiActivity, FiServer, FiDatabase, FiHardDrive, FiClock, FiUserPlus, FiRefreshCw, FiCopy, FiCheck, FiAlertTriangle } from 'react-icons/fi';
import PageHero from './PageHero';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'user',
    subscriptionTier: 'professional'
  });
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createUserResult, setCreateUserResult] = useState(null);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      return;
    }

    const fetchData = async () => {
      try {
        const [dashboardRes, usersRes, activityRes, healthRes] = await Promise.all([
          fetch('/api/admin/dashboard'),
          fetch('/api/admin/users'),
          fetch('/api/admin/activity'),
          fetch('/api/admin/health')
        ]);

        if (dashboardRes.ok) setDashboardData(await dashboardRes.json());
        if (usersRes.ok) setUsers(await usersRes.json());
        if (activityRes.ok) setActivity(await activityRes.json());
        if (healthRes.ok) setHealth(await healthRes.json());
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Only show to super admins
  if (!user || user.role !== 'super_admin') {
    return (
      <div className="wrapper">
        <PageHero
          eyebrow="Admin"
          title="Admin Dashboard"
        />
        <div className="admin-unauthorized">
          <h2>Access Denied</h2>
          <p>This page is only accessible to super administrators.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="wrapper">
        <PageHero
          eyebrow="Admin"
          title="Admin Dashboard"
        />
        <div className="admin-loading">Loading admin dashboard...</div>
      </div>
    );
  }

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const generatePassword = async () => {
    try {
      const response = await fetch('/api/admin/generate-password');
      if (response.ok) {
        const data = await response.json();
        setNewUser({...newUser, password: data.password});
      }
    } catch (error) {
      console.error('Error generating password:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateUserLoading(true);
    
    try {
      const response = await fetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser)
      });
      
      if (response.ok) {
        const data = await response.json();
        setCreateUserResult({
          ...data,
          password: newUser.password // Keep the password for display
        });
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Failed to create user');
    } finally {
      setCreateUserLoading(false);
    }
  };

  const handleResetPassword = async (u) => {
    setResetUser(u);
    setResetLoading(true);
    setResetResult(null);
    try {
      const res = await fetch(`/api/admin/users/${u.id}/reset-password`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setResetResult({
          email: u.username,
          displayName: u.display_name || u.username,
          temporaryPassword: data.temporaryPassword
        });
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.message || 'Failed to reset password');
        setResetUser(null);
      }
    } catch (e) {
      console.error('Reset password failed', e);
      alert('Failed to reset password');
      setResetUser(null);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="wrapper">
      <PageHero
        eyebrow="Admin"
        title="Admin Dashboard"
      />

      <div className="admin-dashboard">

        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
          <button
            className={`admin-tab ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            Activity
          </button>
          <button
            className={`admin-tab ${activeTab === 'system' ? 'active' : ''}`}
            onClick={() => setActiveTab('system')}
          >
            System
          </button>
        </div>

        {activeTab === 'overview' && dashboardData && (
          <div className="admin-stats">
            <div className="stat-card">
              <div className="stat-icon">
                <FiUsers />
              </div>
              <div className="stat-content">
                <h3>Users</h3>
                <div className="stat-number">{dashboardData.users.total_users}</div>
                <div className="stat-details">
                  <span>{dashboardData.users.active_week} active this week</span>
                  <span>{dashboardData.users.active_month} active this month</span>
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <FiImage />
              </div>
              <div className="stat-content">
                <h3>Images</h3>
                <div className="stat-number">{dashboardData.images.total_images.toLocaleString()}</div>
                <div className="stat-details">
                  <span>{formatBytes(dashboardData.images.total_storage)} storage</span>
                  <span>{dashboardData.images.uploaded_week} uploaded this week</span>
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <FiFolderPlus />
              </div>
              <div className="stat-content">
                <h3>Albums</h3>
                <div className="stat-number">{dashboardData.albums.total_albums}</div>
                <div className="stat-details">
                  <span>Total collections</span>
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <FiTag />
              </div>
              <div className="stat-content">
                <h3>Tags</h3>
                <div className="stat-number">{dashboardData.tags.total_tags}</div>
                <div className="stat-details">
                  <span>Unique labels</span>
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <FiShare2 />
              </div>
              <div className="stat-content">
                <h3>Shares</h3>
                <div className="stat-number">{dashboardData.shares.total_shares}</div>
                <div className="stat-details">
                  <span>{dashboardData.shares.total_views.toLocaleString()} total views</span>
                  <span>{dashboardData.shares.shares_week} created this week</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="admin-users">
            <div className="users-header">
              <h2>User Management</h2>
              <button 
                className="btn-create-user"
                onClick={() => setShowCreateUser(true)}
              >
                <FiUserPlus /> Create User
              </button>
            </div>
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Subscription</th>
                    <th>Storage</th>
                    <th>Last Login</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div className="user-info">
                          <div className="user-name">{user.display_name || user.username}</div>
                          <div className="user-email">{user.username}</div>
                        </div>
                      </td>
                      <td>
                        <span className={`role-badge ${user.role}`}>
                          {user.role.replace('_', ' ')}
                        </span>
                      </td>
                      <td>
                        <span className={`subscription-badge ${user.subscription_status}`}>
                          {user.subscription_tier} ({user.subscription_status})
                        </span>
                      </td>
                      <td>
                        <div className="storage-info">
                          <div>{formatBytes(user.storage_used)} / {formatBytes(user.storage_limit)}</div>
                          <div className="storage-bar">
                            <div 
                              className="storage-fill" 
                              style={{ width: `${(user.storage_used / user.storage_limit) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td>{user.last_login ? formatDate(user.last_login) : 'Never'}</td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        <div className="user-actions">
                          <button
                            className="btn-reset"
                            onClick={() => handleResetPassword(user)}
                            disabled={resetLoading && resetUser?.id === user.id}
                          >
                            {resetLoading && resetUser?.id === user.id ? 'Resetting…' : 'Reset Password'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {showCreateUser && (
              <div className="create-user-modal">
                <div className="modal-overlay" onClick={() => !createUserLoading && setShowCreateUser(false)}></div>
                <div className="modal-content">
                  <h3>Create New User Account</h3>
                  
                  {createUserResult ? (
                    <div className="create-user-success">
                      <div className="success-icon"><FiCheck /></div>
                      <h4>User Created Successfully!</h4>
                      <div className="user-credentials">
                        <p><strong>Email:</strong> {createUserResult.email}</p>
                        <p><strong>Password:</strong> {createUserResult.password}</p>
                        <p><strong>Role:</strong> {createUserResult.role}</p>
                        <p><strong>Subscription:</strong> {createUserResult.subscriptionTier}</p>
                      </div>
                      <div className="credential-actions">
                        <button 
                          className="btn-copy"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              `Email: ${createUserResult.email}\nPassword: ${createUserResult.password}`
                            );
                            setPasswordCopied(true);
                            setTimeout(() => setPasswordCopied(false), 2000);
                          }}
                        >
                          {passwordCopied ? <><FiCheck /> Copied!</> : <><FiCopy /> Copy Credentials</>}
                        </button>
                        <button 
                          className="btn-done"
                          onClick={() => {
                            setShowCreateUser(false);
                            setCreateUserResult(null);
                            setNewUser({
                              email: '',
                              password: '',
                              displayName: '',
                              role: 'user',
                              subscriptionTier: 'professional'
                            });
                            // Refresh users list
                            fetch('/api/admin/users')
                              .then(res => res.json())
                              .then(data => setUsers(data));
                          }}
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleCreateUser}>
                      <div className="form-group">
                        <label>Email Address *</label>
                        <input
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                          required
                          placeholder="user@example.com"
                        />
                      </div>

                      <div className="form-group">
                        <label>Password *</label>
                        <div className="password-input">
                          <input
                            type="text"
                            value={newUser.password}
                            onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                            required
                            placeholder="Enter password or generate"
                          />
                          <button
                            type="button"
                            className="btn-generate"
                            onClick={generatePassword}
                          >
                            <FiRefreshCw /> Generate
                          </button>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Display Name</label>
                        <input
                          type="text"
                          value={newUser.displayName}
                          onChange={(e) => setNewUser({...newUser, displayName: e.target.value})}
                          placeholder="Optional display name"
                        />
                      </div>

                      <div className="form-group">
                        <label>Role</label>
                        <select
                          value={newUser.role}
                          onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Subscription Tier</label>
                        <select
                          value={newUser.subscriptionTier}
                          onChange={(e) => setNewUser({...newUser, subscriptionTier: e.target.value})}
                        >
                          <option value="starter">Starter (100GB)</option>
                          <option value="professional">Professional (500GB)</option>
                          <option value="studio">Studio (2TB)</option>
                        </select>
                      </div>

                      <div className="modal-actions">
                        <button
                          type="button"
                          className="btn-cancel"
                          onClick={() => setShowCreateUser(false)}
                          disabled={createUserLoading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-primary"
                          disabled={createUserLoading}
                        >
                          {createUserLoading ? 'Creating...' : 'Create User'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {resetUser && (
          <div className="create-user-modal">
            <div className="modal-overlay" onClick={() => !resetLoading && setResetUser(null)}></div>
            <div className="modal-content">
              <h3>Reset Password</h3>
              {!resetResult ? (
                <div className="admin-loading">{resetLoading ? 'Generating a new password…' : ''}</div>
              ) : (
                <div className="create-user-success">
                  <div className="success-icon"><FiCheck /></div>
                  <h4>Password Reset Successfully</h4>
                  <div className="user-credentials">
                    <p><strong>User:</strong> {resetResult.displayName} ({resetResult.email})</p>
                    <p><strong>Temporary Password:</strong> {resetResult.temporaryPassword}</p>
                  </div>
                  <div className="credential-actions">
                    <button
                      className="btn-copy"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `Email: ${resetResult.email}\nTemporary Password: ${resetResult.temporaryPassword}`
                        );
                        setPasswordCopied(true);
                        setTimeout(() => setPasswordCopied(false), 2000);
                      }}
                    >
                      {passwordCopied ? <><FiCheck /> Copied!</> : <><FiCopy /> Copy Details</>}
                    </button>
                    <button className="btn-done" onClick={() => { setResetUser(null); setResetResult(null); }}>Done</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="admin-activity">
            <h2>Recent Activity</h2>
            <div className="activity-feed">
              {activity.map((item, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-icon">
                    {item.activity_type === 'share_created' ? <FiShare2 /> : <FiImage />}
                  </div>
                  <div className="activity-content">
                    <div className="activity-text">
                      <strong>{item.user_name}</strong>{' '}
                      {item.activity_type === 'share_created' 
                        ? `shared album "${item.album_title}"` 
                        : `uploaded "${item.image_filename}"`
                      }
                      {item.view_count && ` (${item.view_count} views)`}
                    </div>
                    <div className="activity-time">{formatDate(item.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'system' && health && (
          <div className="admin-system">
            <h2>System Health</h2>
            <div className="system-stats">
              {health.diskUsage && (
                <div className={`system-card ${health.diskUsage.percentUsed > 80 ? 'warning' : ''}`}>
                  <div className="system-icon">
                    {health.diskUsage.percentUsed > 80 ? <FiAlertTriangle /> : <FiHardDrive />}
                  </div>
                  <div className="system-content">
                    <h3>System Disk</h3>
                    <div className={`system-status ${health.diskUsage.percentUsed > 80 ? 'warning' : health.diskUsage.percentUsed > 60 ? 'caution' : 'healthy'}`}>
                      {formatBytes(health.diskUsage.available)} free of {formatBytes(health.diskUsage.total)}
                    </div>
                    <div className="storage-bar">
                      <div 
                        className={`storage-fill ${health.diskUsage.percentUsed > 80 ? 'critical' : ''}`}
                        style={{ width: `${health.diskUsage.percentUsed}%` }}
                      ></div>
                    </div>
                    <div className="system-details">
                      {health.diskUsage.percentUsed}% used ({formatBytes(health.diskUsage.used)})
                    </div>
                  </div>
                </div>
              )}

              <div className="system-card">
                <div className="system-icon">
                  <FiDatabase />
                </div>
                <div className="system-content">
                  <h3>Database</h3>
                  <div className={`system-status ${health.database === 'connected' ? 'healthy' : 'error'}`}>
                    {health.database}
                  </div>
                </div>
              </div>

              <div className="system-card">
                <div className="system-icon">
                  <FiServer />
                </div>
                <div className="system-content">
                  <h3>Server Uptime</h3>
                  <div className="system-status healthy">
                    {Math.floor(health.uptime / 3600)}h {Math.floor((health.uptime % 3600) / 60)}m
                  </div>
                </div>
              </div>

              <div className="system-card">
                <div className="system-icon">
                  <FiHardDrive />
                </div>
                <div className="system-content">
                  <h3>User Storage</h3>
                  <div className="system-status">
                    {formatBytes(health.storage.total_used)} / {formatBytes(health.storage.total_limit)}
                  </div>
                </div>
              </div>

              <div className="system-card">
                <div className="system-icon">
                  <FiActivity />
                </div>
                <div className="system-content">
                  <h3>Memory Usage</h3>
                  <div className="system-status">
                    {formatBytes(health.memory.heapUsed)} / {formatBytes(health.memory.heapTotal)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
